
import { formatSchemaDate } from './seoSchema';

interface PageSEOProps {
  title: string;
  description: string;
  type?: 'WebPage' | 'Article' | 'Product' | 'Service' | 'Organization';
  imageUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  canonicalUrl: string;
  language: string;
  keywords?: string;
  breadcrumbItems?: Array<{name: string, url: string}>;
  industry?: string;
  city?: string;
  author?: string;
}

/**
 * Enhanced schema markup generator that supports multiple schema types
 * and implements breadcrumbs schema markup
 */
export const generateEnhancedSchema = ({
  title,
  description,
  type = 'WebPage',
  imageUrl = "https://future-level.ch/opengraph-image-p98pqg.png",
  publishDate,
  modifiedDate,
  canonicalUrl,
  language,
  keywords = "",
  breadcrumbItems = [],
  industry,
  city,
  author
}: PageSEOProps): void => {
  
  // Base schema that applies to all types
  const baseSchema: any = {
    "@context": "https://schema.org",
    "@type": type,
    "name": title,
    "description": description,
    "url": canonicalUrl,
    "inLanguage": language,
    "image": imageUrl,
  };

  // Add type-specific properties
  if (type === 'Article' && publishDate) {
    baseSchema.headline = title;
    baseSchema.datePublished = formatSchemaDate(publishDate);
    baseSchema.dateModified = modifiedDate ? formatSchemaDate(modifiedDate) : formatSchemaDate(publishDate);
    baseSchema.author = {
      "@type": "Organization",
      "name": "Future Level",
      "url": "https://future-level.ch"
    };
    baseSchema.publisher = {
      "@type": "Organization",
      "name": "Future Level",
      "logo": {
        "@type": "ImageObject",
        "url": "https://future-level.ch/logo.png"
      }
    };
    baseSchema.keywords = keywords;
  } else if (type === 'Product' || type === 'Service') {
    baseSchema.provider = {
      "@type": "Organization",
      "name": "Future Level",
      "url": "https://future-level.ch"
    };
    
    // Add industry-specific service details
    if (industry && type === 'Service') {
      const industryKeywords = {
        'finance': 'KI-Videomarketing Finanzsektor, Banken Marketing Videos KI, Versicherung Erklärvideo',
        'luxury': 'Luxusmarken KI-Videos, Premium Produktvideos, Hochwertiges Videomarketing',
        'tourism': 'Tourismus KI-Videos, Hotel Marketingvideos, Reisevideos KI-erstellt',
        'healthcare': 'Gesundheitswesen KI-Videos, Klinik Marketing Videos, Praxis Erklärvideo',
        'ecommerce': 'E-Commerce KI-Videos, Produktvideos Online-Shop, Conversion Produktvideos',
      };
      
      baseSchema.keywords = keywords || industryKeywords[industry as keyof typeof industryKeywords] || 'KI-Videomarketing, AI-generierte Videos';
      
      // Add service-specific offerings
      baseSchema.offers = {
        "@type": "Offer",
        "description": language === 'de' ? `KI-generierte Marketingvideos für ${industry}` :
                       language === 'en' ? `AI-generated marketing videos for ${industry}` :
                       language === 'fr' ? `Vidéos marketing générées par IA pour ${industry}` :
                       `Video marketing generati da IA per ${industry}`
      };
      
      // Add audience targeting
      baseSchema.audience = {
        "@type": "Audience",
        "audienceType": industry === 'finance' ? "Financial Services" :
                       industry === 'luxury' ? "Luxury Brands" :
                       industry === 'tourism' ? "Tourism Providers" :
                       industry === 'healthcare' ? "Healthcare Providers" :
                       industry === 'ecommerce' ? "E-Commerce Businesses" : "Businesses"
      };
    }
    
    // Add location-specific information if a city is provided
    if (city && type === 'Service') {
      const cityTranslations: {[key: string]: {de: string, en: string, fr: string, it: string}} = {
        'zurich': { de: 'Zürich', en: 'Zurich', fr: 'Zurich', it: 'Zurigo' },
        'geneva': { de: 'Genf', en: 'Geneva', fr: 'Genève', it: 'Ginevra' },
        'basel': { de: 'Basel', en: 'Basel', fr: 'Bâle', it: 'Basilea' },
        'bern': { de: 'Bern', en: 'Bern', fr: 'Berne', it: 'Berna' }
      };
      
      const cityName = cityTranslations[city.toLowerCase()]?.[language as keyof typeof cityTranslations[typeof city]] || city;
      
      baseSchema.areaServed = {
        "@type": "City",
        "name": cityName
      };
      
      // Update name and description with city information
      baseSchema.name = `${baseSchema.name} ${cityName}`;
      baseSchema.description = `${baseSchema.description} ${language === 'de' ? `in ${cityName}` :
                                                           language === 'en' ? `in ${cityName}` :
                                                           language === 'fr' ? `à ${cityName}` :
                                                           `a ${cityName}`}`;
    }
  } else if (type === 'Organization') {
    baseSchema.logo = "https://future-level.ch/logo.png";
    baseSchema.sameAs = [
      "https://facebook.com/futurelevel",
      "https://instagram.com/futurelevel",
      "https://linkedin.com/company/futurelevel"
    ];
    
    // Add author information if provided
    if (author) {
      baseSchema.founder = {
        "@type": "Person",
        "name": author
      };
    }
    
    baseSchema.address = {
      "@type": "PostalAddress",
      "addressLocality": "Emmen",
      "addressRegion": "LU",
      "addressCountry": "CH",
      "postalCode": "6020",
      "streetAddress": "Seetalstrasse 42"
    };
    baseSchema.contactPoint = {
      "@type": "ContactPoint",
      "telephone": "+41 41 123 45 67",
      "email": "info@future-level.ch",
      "contactType": "customer service"
    };
  }

  // Add breadcrumbs schema if provided
  let schemas = [baseSchema];
  
  if (breadcrumbItems && breadcrumbItems.length > 0) {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };

    schemas.push(breadcrumbSchema);
  }

  // Inject all schemas
  injectSchema(schemas);
};

/**
 * Helper function to inject schema(s) into the document head
 */
const injectSchema = (schema: any): void => {
  let scriptTag = document.getElementById('schema-markup') as HTMLScriptElement | null;
  
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.id = 'schema-markup';
    scriptTag.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptTag);
  }
  
  scriptTag.textContent = JSON.stringify(schema);
};

/**
 * Generate breadcrumb data for schema markup and UI breadcrumbs
 */
export const generateBreadcrumbData = (
  pagePath: string,
  pageTitle: string,
  language: string,
  parentPaths: Array<{path: string, title: string}> = []
): Array<{name: string, url: string}> => {
  const baseUrl = 'https://future-level.ch';
  
  // Home page is always the first item
  const breadcrumbs: Array<{name: string, url: string}> = [
    {
      name: language === 'de' ? 'Startseite' : 
            language === 'fr' ? 'Accueil' : 
            language === 'it' ? 'Home' : 'Home',
      url: `${baseUrl}/${language}`
    }
  ];
  
  // Add any parent paths
  parentPaths.forEach(parent => {
    breadcrumbs.push({
      name: parent.title,
      url: `${baseUrl}/${language}${parent.path}`
    });
  });
  
  // Add current page as the last item
  breadcrumbs.push({
    name: pageTitle,
    url: `${baseUrl}/${language}${pagePath}`
  });
  
  return breadcrumbs;
};

/**
 * Generate industry-specific rich results for service pages
 * Based on keyword research to target specific industries
 */
export const generateServicePageSchema = (
  industry: 'finance' | 'luxury' | 'tourism' | 'healthcare' | 'ecommerce',
  language: string,
  canonicalUrl: string,
  city?: string
): void => {
  // Industry-specific data based on keyword research
  const industryData: {[key: string]: {title: string, description: string}} = {
    finance: {
      title: language === 'de' ? 'KI-Videomarketing für Finanzdienstleister' :
             language === 'en' ? 'AI Video Marketing for Financial Services' :
             language === 'fr' ? 'Marketing Vidéo IA pour Services Financiers' :
             'Marketing Video AI per Servizi Finanziari',
      description: language === 'de' ? 'Professionelle KI-generierte Videos für Banken, Versicherungen und Finanzberater mit Fokus auf Compliance und Vertrauen.' :
                   language === 'en' ? 'Professional AI-generated videos for banks, insurance companies, and financial advisors focusing on compliance and trust.' :
                   language === 'fr' ? 'Vidéos professionnelles générées par IA pour banques, assurances et conseillers financiers, axées sur la conformité et la confiance.' :
                   'Video professionali generati da IA per banche, assicurazioni e consulenti finanziari, con focus su conformità e fiducia.'
    },
    luxury: {
      title: language === 'de' ? 'KI-Videomarketing für Luxusmarken' :
             language === 'en' ? 'AI Video Marketing for Luxury Brands' :
             language === 'fr' ? 'Marketing Vidéo IA pour Marques de Luxe' :
             'Marketing Video AI per Marchi di Lusso',
      description: language === 'de' ? 'Premium KI-Videoinhalte für Luxusmarken und hochwertige Produkte - Exklusiv für Schweizer Premiummarken konzipiert.' :
                   language === 'en' ? 'Premium AI video content for luxury brands and high-end products - Exclusively designed for Swiss premium brands.' :
                   language === 'fr' ? 'Contenu vidéo IA premium pour marques de luxe et produits haut de gamme - Conçu exclusivement pour les marques premium suisses.' :
                   'Contenuto video AI premium per marchi di lusso e prodotti di alta gamma - Progettato esclusivamente per marchi premium svizzeri.'
    },
    tourism: {
      title: language === 'de' ? 'KI-Videomarketing für Tourismus' :
             language === 'en' ? 'AI Video Marketing for Tourism' :
             language === 'fr' ? 'Marketing Vidéo IA pour Tourisme' :
             'Marketing Video AI per Turismo',
      description: language === 'de' ? 'Inspirierende KI-Videos für Hotels, Reiseveranstalter und Tourismusverbände - Förderung von Reisezielen in der Schweiz.' :
                   language === 'en' ? 'Inspiring AI videos for hotels, tour operators, and tourism boards - Promoting destinations in Switzerland.' :
                   language === 'fr' ? 'Vidéos IA inspirantes pour hôtels, voyagistes et offices du tourisme - Promotion des destinations en Suisse.' :
                   'Video AI ispiranti per hotel, operatori turistici e enti del turismo - Promozione delle destinazioni in Svizzera.'
    },
    healthcare: {
      title: language === 'de' ? 'KI-Videomarketing für Gesundheitswesen' :
             language === 'en' ? 'AI Video Marketing for Healthcare' :
             language === 'fr' ? 'Marketing Vidéo IA pour Santé' :
             'Marketing Video AI per Sanità',
      description: language === 'de' ? 'Vertrauenswürdige KI-Videos für Kliniken, Praxen und Gesundheitsdienstleister - DSGVO-konform und ethisch verantwortlich.' :
                   language === 'en' ? 'Trustworthy AI videos for clinics, practices, and healthcare providers - GDPR compliant and ethically responsible.' :
                   language === 'fr' ? 'Vidéos IA fiables pour cliniques, cabinets et prestataires de santé - Conformes au RGPD et éthiquement responsables.' :
                   'Video AI affidabili per cliniche, studi medici e fornitori di servizi sanitari - Conformi al GDPR e eticamente responsabili.'
    },
    ecommerce: {
      title: language === 'de' ? 'KI-Videomarketing für E-Commerce' :
             language === 'en' ? 'AI Video Marketing for E-Commerce' :
             language === 'fr' ? 'Marketing Vidéo IA pour E-Commerce' :
             'Marketing Video AI per E-Commerce',
      description: language === 'de' ? 'Konversionsstarke KI-Produktvideos für Online-Shops und E-Commerce - Steigern Sie Verkäufe mit dynamischen Produktpräsentationen.' :
                   language === 'en' ? 'High-converting AI product videos for online shops and e-commerce - Boost sales with dynamic product presentations.' :
                   language === 'fr' ? 'Vidéos IA de produits à fort taux de conversion pour boutiques en ligne et e-commerce - Augmentez vos ventes avec des présentations de produits dynamiques.' :
                   'Video AI di prodotti ad alta conversione per negozi online e e-commerce - Aumenta le vendite con presentazioni dinamiche dei prodotti.'
    }
  };
  
  const data = industryData[industry];
  
  // Create breadcrumb items for service pages
  let parentPaths = [{
    path: language === 'de' ? '/leistungen' : 
          language === 'en' ? '/services' : 
          language === 'fr' ? '/services' : 
          '/servizi',
    title: language === 'de' ? 'Leistungen' : 
           language === 'en' ? 'Services' : 
           language === 'fr' ? 'Services' : 
           'Servizi'
  }];
  
  // Extract the page path from the canonical URL
  const urlObj = new URL(canonicalUrl);
  const pagePath = urlObj.pathname.replace(`/${language}`, '');
  
  // Generate standard breadcrumbs
  const breadcrumbItems = generateBreadcrumbData(
    pagePath,
    data.title,
    language,
    parentPaths
  );
  
  // Generate the full schema for the service page
  generateEnhancedSchema({
    title: city ? `${data.title} ${city}` : data.title,
    description: data.description,
    type: 'Service',
    canonicalUrl: canonicalUrl,
    language: language,
    breadcrumbItems: breadcrumbItems,
    industry: industry,
    city: city
  });
};

